import { useAudit } from '@api/audits';
import { useCompany } from '@api/company';
import { MemberRole } from '@api/types/enums';
import { useGetUser } from '@api/user/hooks';
import { useGetSuperadminCompany } from '@shared/api/superadminCompany';
import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';

import {
  getPageTitle,
  isCompanyPathname,
  isPlatformAdminCompanyPathname,
} from './lib/getPageTitle';

/**
 * Wider than `PageTitleRouteParams`: this is the `useParams` shape across every private route the
 * manager is mounted under, not just the params the title resolver reads.
 */
interface RouteParams {
  id?: string;
  path?: string;
  documentId?: string;
  meetingId?: string;
  requestId?: string;
  type?: string;
  idTeam?: string;
  idRole?: string;
}

const paramsCompany = {
  auditors: true,
};

export const PageTitleManager = () => {
  const location = useLocation();
  const params = useParams<RouteParams>();

  // `:id` means an audit on dashboard routes, a company on `/company/` routes and, on platform
  // admin routes, whatever `:type` names (see `@shared/api/superadminCompany`), so the title reads
  // one of the three per pathname — never two.
  const isCompanyPage = isCompanyPathname(location.pathname);
  const isAdminCompanyPage = isPlatformAdminCompanyPathname(location.pathname);
  // The audit is the dashboard layout's to read, and it reads it only once the caller may: an
  // auditor who has not confirmed independence is asked first, and the server refuses their read
  // until they answer (the independence 409). The title waits for that read instead of sending its
  // own.
  const { data: auditData } = useAudit(params.id, false);
  const { data: companyResponse } = useCompany(
    params.id,
    paramsCompany,
    !!params.id && isCompanyPage && !isAdminCompanyPage,
  );
  // Only a super-admin reaches the page; anyone else's read of it would be a 403 and a logout.
  const isSuperadmin = useGetUser().data?.user?.role === MemberRole.SUPER_ADMIN;
  const { data: adminCompany } = useGetSuperadminCompany(
    params.type ?? '',
    params.id ?? '',
    isAdminCompanyPage && isSuperadmin,
  );

  const title = getPageTitle({
    pathname: location.pathname,
    search: location.search,
    params,
    auditCompanyName: auditData?.audit.company?.name,
    auditName: auditData?.audit.name,
    companyName: isAdminCompanyPage ? adminCompany?.name : companyResponse?.company.name,
  });

  return (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  );
};
